import { MASKED_VALUE } from '../constants';
import { isJsonString, JsonValue } from '../utils/json';

type Config = Record<string, JsonValue | undefined>;

/** `config` with each set sensitive field replaced by `MASKED_VALUE`. */
export const maskSensitiveFields = <T extends Config>(
  config: T,
  fields: readonly string[],
): T => {
  const masks: Record<string, string> = {};

  for (const field of fields) {
    if (config[field] !== undefined && config[field] !== null) {
      masks[field] = MASKED_VALUE;
    }
  }

  return { ...config, ...masks };
};

/**
 * `incoming` with each sensitive field that is masked or unset restored from
 * `existing`, so a client that only ever sees masked values can't clobber them.
 */
export const restoreSensitiveFields = <T extends Config>(
  incoming: T,
  existing: Config | undefined,
  fields: readonly string[],
): T => {
  const restores: Config = {};

  for (const field of fields) {
    const value = incoming[field];

    if (value === MASKED_VALUE || value === undefined || value === null) {
      restores[field] = existing?.[field];
    }
  }

  return { ...incoming, ...restores };
};

/**
 * `config` without the sensitive fields the user left masked or blank (React
 * Admin sends a cleared input as `null`), so the backend keeps their stored
 * values.
 */
export const omitUnchangedSensitiveFields = <T extends Config>(
  config: T,
  fields: readonly string[],
): T => {
  const changed = { ...config };

  for (const field of fields) {
    const value = config[field];

    if (
      value === MASKED_VALUE ||
      value === null ||
      (isJsonString(value) && value.trim() === '')
    ) {
      delete changed[field];
    }
  }

  return changed;
};
